import React, { useEffect, useState } from 'react';
import { Card, Table, Badge, Form, Spinner, Alert, Tabs, Tab } from 'react-bootstrap';
import { FaChartLine, FaTrophy, FaTachometerAlt, FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../styles/Flota.css';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

const FlotaActividad = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [periodo, setPeriodo] = useState('30');
  const navigate = useNavigate();

  useEffect(() => {
    cargarActividad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo]);

  const cargarActividad = async () => {
    try {
      setLoading(true);
      setError(null);

      const authToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;

      if (!authToken || !user) {
        setError('No hay sesión activa');
        return;
      }

      const userId = user.Legajo || user.CdEmpleado || user.CdUsuario;
      const userRole = user.IdRol || user.tipoUsuario || 1;

      const response = await axios.get(`${API_URL}/api/flota/actividad?periodo=${periodo}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'x-user-id': String(userId),
          'x-user-role': String(userRole)
        }
      });

      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError('No se pudo cargar la actividad');
      }
    } catch (err) {
      console.error('Error al cargar actividad:', err);
      setError(err.response?.data?.error || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-AR').format(Math.round(num));
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'Sin registro';
    return new Date(fecha).toLocaleDateString('es-AR');
  };

  const verDetalle = (interno) => {
    navigate(`/administrador/flota/unidades/${interno}`);
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Cargando análisis de actividad...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  if (!data) return null;

  return (
    <div className="flota-actividad">
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2><FaChartLine className="me-2" />Actividad y Uso de Flota</h2>
            <p className="text-muted mb-0">Análisis de rendimiento y utilización de unidades</p>
          </div>
          <Form.Group style={{ minWidth: '200px' }}>
            <Form.Select 
              value={periodo} 
              onChange={(e) => setPeriodo(e.target.value)}
            >
              <option value="7">Últimos 7 días</option>
              <option value="15">Últimos 15 días</option>
              <option value="30">Últimos 30 días</option>
              <option value="60">Últimos 60 días</option>
              <option value="90">Últimos 90 días</option>
            </Form.Select>
          </Form.Group>
        </div>
      </div>

      {/* Tabs con rankings */}
      <Tabs defaultActiveKey="dias" className="mb-4">
        {/* Ranking por días activos */}
        <Tab 
          eventKey="dias" 
          title={
            <span>
              <FaTrophy className="me-2" />
              Por Días Activos
            </span>
          }
        >
          <Card>
            <Card.Header className="bg-primary text-white">
              <strong>Ranking de Unidades por Días Activos ({periodo} días)</strong>
            </Card.Header>
            <Card.Body>
              {data.rankingDias.length === 0 ? (
                <Alert variant="info">No hay datos para el período seleccionado</Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th className="text-center">#</th>
                        <th>Interno</th>
                        <th>Dominio</th>
                        <th>Estado</th>
                        <th className="text-center">Días Activos</th>
                        <th className="text-end">Total Km</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rankingDias.map((item, idx) => (
                        <tr key={item.Interno}>
                          <td className="text-center">
                            <strong>
                              {idx < 3 ? (
                                <FaTrophy 
                                  className={
                                    idx === 0 ? 'text-warning' : 
                                    idx === 1 ? 'text-secondary' : 
                                    'text-bronze'
                                  } 
                                />
                              ) : (
                                idx + 1
                              )}
                            </strong>
                          </td>
                          <td><strong>{item.Interno}</strong></td>
                          <td>{item.Dominio || '-'}</td>
                          <td>
                            <Badge bg="secondary">{item.NmEstado || 'Sin estado'}</Badge>
                          </td>
                          <td className="text-center">
                            <Badge 
                              bg={item.DiasActivos > 20 ? 'success' : item.DiasActivos > 10 ? 'warning' : 'danger'}
                              style={{ fontSize: '1rem' }}
                            >
                              {item.DiasActivos}
                            </Badge>
                          </td>
                          <td className="text-end">{formatNumber(item.TotalKms)} km</td>
                          <td className="text-center">
                            <Badge 
                              bg="primary" 
                              style={{ cursor: 'pointer' }}
                              onClick={() => verDetalle(item.Interno)}
                            >
                              <FaEye /> Ver
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Ranking por kilómetros */}
        <Tab 
          eventKey="kms" 
          title={
            <span>
              <FaTachometerAlt className="me-2" />
              Por Kilómetros
            </span>
          }
        >
          <Card>
            <Card.Header className="bg-info text-white">
              <strong>Ranking de Unidades por Kilómetros ({periodo} días)</strong>
            </Card.Header>
            <Card.Body>
              {data.rankingKms.length === 0 ? (
                <Alert variant="info">No hay datos para el período seleccionado</Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th className="text-center">#</th>
                        <th>Interno</th>
                        <th>Dominio</th>
                        <th>Estado</th>
                        <th className="text-center">Días Activos</th>
                        <th className="text-end">Total Km</th>
                        <th className="text-end">Promedio Km/día</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rankingKms.map((item, idx) => (
                        <tr key={item.Interno}>
                          <td className="text-center">
                            <strong>
                              {idx < 3 ? (
                                <FaTrophy 
                                  className={
                                    idx === 0 ? 'text-warning' : 
                                    idx === 1 ? 'text-secondary' : 
                                    'text-bronze'
                                  } 
                                />
                              ) : (
                                idx + 1
                              )}
                            </strong>
                          </td>
                          <td><strong>{item.Interno}</strong></td>
                          <td>{item.Dominio || '-'}</td>
                          <td>
                            <Badge bg="secondary">{item.NmEstado || 'Sin estado'}</Badge>
                          </td>
                          <td className="text-center">
                            <Badge bg="primary">{item.DiasActivos}</Badge>
                          </td>
                          <td className="text-end">
                            <strong className="text-info">{formatNumber(item.TotalKms)} km</strong>
                          </td>
                          <td className="text-end">{formatNumber(item.PromedioKmsPorDia)} km</td>
                          <td className="text-center">
                            <Badge 
                              bg="primary" 
                              style={{ cursor: 'pointer' }}
                              onClick={() => verDetalle(item.Interno)}
                            >
                              <FaEye /> Ver
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Actividad por día */}
        <Tab 
          eventKey="diario" 
          title={
            <span>
              <FaChartLine className="me-2" />
              Actividad Diaria
            </span>
          }
        >
          <Card>
            <Card.Header className="bg-success text-white">
              <strong>Actividad Diaria - Unidades en Servicio ({periodo} días)</strong>
            </Card.Header>
            <Card.Body>
              {data.actividadPorDia.length === 0 ? (
                <Alert variant="info">No hay datos para el período seleccionado</Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th className="text-center">Cantidad de Unidades</th>
                        <th className="text-end">Total Kilómetros</th>
                        <th className="text-end">Promedio Km/Unidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.actividadPorDia.map((item) => {
                        const promedioKm = item.CantidadUnidades > 0 
                          ? item.TotalKms / item.CantidadUnidades 
                          : 0;
                        
                        return (
                          <tr key={item.Fecha}>
                            <td><strong>{formatFecha(item.Fecha)}</strong></td>
                            <td className="text-center">
                              <Badge bg="primary" style={{ fontSize: '0.95rem' }}>
                                {item.CantidadUnidades}
                              </Badge>
                            </td>
                            <td className="text-end">{formatNumber(item.TotalKms)} km</td>
                            <td className="text-end text-info">
                              {formatNumber(promedioKm)} km
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </div>
  );
};

export default FlotaActividad;
